/* eslint-disable sort-keys */
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  Box,
  Button,
  Collapse,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery
} from '@mui/material'
import { or, not, prop, reverse, slice, sortBy, length, isEmpty } from 'ramda'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'

const AllTopicsBlock = () => {
  const isTablet = useMediaQuery(theme => theme.breakpoints.down('md'))
  const [isExpanded, setIsExpanded] = useState(false)

  const { userDomains } = useSelector(prop('user'))

  useEffect(() => {
    if (isTablet) {
      setIsExpanded(true)
    }
    setIsExpanded(false)
  }, [isTablet])

  if (isEmpty(userDomains))
    return (
      <Stack
        data-testid="skeleton"
        direction={{ xs: 'column', lg: 'row' }}
        spacing={{ xs: 1, lg: 3 }}
      >
        <Skeleton height={200} variant="rectangular" width="100%" />
        <Skeleton height={200} variant="rectangular" width="100%" />
        <Skeleton height={200} variant="rectangular" width="100%" />
      </Stack>
    )

  const domainSubtext = {
    Matter: (
      <FormattedMessage defaultMessage="connections & purpose" id="XY6yEK" />
    ),
    Succeed: <FormattedMessage defaultMessage="life & career" id="xkryKP" />,
    Thrive: <FormattedMessage defaultMessage="mind & body" id="Q2hKy0" />
  }

  const sortSubdomains = subs => reverse(sortBy(prop('sort'))(subs))

  return (
    <Stack>
      <Box
        sx={{
          display: 'grid',
          gridGap: 16,
          // eslint-disable-next-line sort-keys
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          px: 3
        }}
      >
        {userDomains.map(
          ({ id, title, subdomains, frontend_url_slug: slug }) => (
            <Box key={id}>
              <Stack
                alignItems={{ xs: 'flex-start', md: 'center' }}
                direction={{ xs: 'column', md: 'row' }}
                spacing={0.5}
              >
                <Typography
                  noWrap
                  sx={{ color: 'school.primary', fontFamily: 'mark-ot-bold' }}
                >
                  <FormattedMessage
                    defaultMessage="{title}"
                    id="2YgLVn"
                    values={{ title }}
                  />
                  {not(isTablet) && ' /'}
                </Typography>
                <Typography noWrap variant="domainSubtitle">
                  {prop(title, domainSubtext)}
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {slice(0, 3, sortSubdomains(subdomains)).map(
                  ({ id, title, frontend_url_slug }) => (
                    <Typography
                      aria-label={`${slug} ${frontend_url_slug}`}
                      color="primary.main"
                      component={Link}
                      key={title}
                      state={{ groupId: id }}
                      sx={{
                        display: 'block',
                        fontSize: 14
                      }}
                      to={`/${slug}/${frontend_url_slug}`}
                      variant="body2"
                    >
                      {title}
                    </Typography>
                  )
                )}
                <Collapse in={or(isExpanded, isTablet)}>
                  {slice(3, length(subdomains), sortSubdomains(subdomains)).map(
                    ({ id, title, frontend_url_slug }) => (
                      <Typography
                        aria-label={`${slug} ${frontend_url_slug}`}
                        color="primary.main"
                        component={Link}
                        key={title}
                        state={{ groupId: id }}
                        sx={{
                          display: 'block',
                          fontSize: 14,
                          pb: 1
                        }}
                        to={`/${slug}/${frontend_url_slug}`}
                        variant="body3"
                      >
                        {title}
                      </Typography>
                    )
                  )}
                </Collapse>
              </Stack>
            </Box>
          )
        )}
      </Box>

      <Button
        endIcon={
          isExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />
        }
        onClick={() => setIsExpanded(not(isExpanded))}
        sx={{
          alignSelf: 'center',
          // eslint-disable-next-line sort-keys
          display: { xs: 'none', md: 'flex' },
          mt: 3
        }}
        variant="text"
      >
        {isExpanded ? (
          <FormattedMessage defaultMessage="show less" id="vb8spb" />
        ) : (
          <FormattedMessage defaultMessage="show more" id="IRPFF9" />
        )}
      </Button>
    </Stack>
  )
}

export default AllTopicsBlock
